import {
  addDoc,
  collection,
  doc,
  getDocs,
  limit,
  orderBy,
  query,
  serverTimestamp,
  updateDoc,
  where,
  writeBatch,
} from 'firebase/firestore';
import { db } from '../firebase';

export type UserRole =
  | 'admin'
  | 'cap'
  | 'ae'
  | 'responsible'
  | 'teacher'
  | 'cgs'
  | 'drh'
  | 'UNKNOWN';

export interface UserRow {
  id: string;
  authUid?: string;
  firstName?: string;
  lastName?: string;
  phone?: string;
  academy?: string;
  cap?: string;
  region?: string;
  commune?: string;
  speciality?: string;
  email?: string;
  role?: UserRole;
  status?: 'active' | 'inactive';
  createdAt?: Date;
  updatedAt?: Date;
}

export interface UpdateUserPayload {
  authUid?: string;
  firstName?: string;
  lastName?: string;
  email?: string | null;
  phone?: string;
  academy?: string;
  cap?: string;
  region?: string;
  commune?: string;
  speciality?: string;
  role?: Exclude<UserRole, 'UNKNOWN'>;
  status?: 'active' | 'inactive';
}

export interface ImportedUserRow {
  authUid?: string;
  firstName: string;
  lastName: string;
  email?: string;
  phone?: string;
  academy?: string;
  cap?: string;
  region?: string;
  commune?: string;
  speciality?: string;
  role: Exclude<UserRole, 'UNKNOWN'>;
  status: 'active' | 'inactive';
}

const COLLECTION = 'users';

export async function listUsers(): Promise<UserRow[]> {
  const snap = await getDocs(
    query(collection(db, COLLECTION), orderBy('lastName', 'asc'))
  );

  return snap.docs.map((d) => {
    const data = d.data() as any;
    return {
      id: d.id,
      authUid: data.authUid,
      firstName: data.firstName,
      lastName: data.lastName,
      phone: data.phone,
      academy: data.academy,
      cap: data.cap,
      region: data.region,
      commune: data.commune,
      speciality: data.speciality,
      email: data.email,
      role: (data.role as UserRole | undefined) ?? 'UNKNOWN',
      status: data.status,
      createdAt: data.createdAt?.toDate ? data.createdAt.toDate() : undefined,
      updatedAt: data.updatedAt?.toDate ? data.updatedAt.toDate() : undefined,
    };
  });
}

export async function toggleUserStatus(id: string, current?: string): Promise<void> {
  const ref = doc(db, COLLECTION, id);
  const next = current === 'active' ? 'inactive' : 'active';
  await updateDoc(ref, { status: next });
}

export async function updateUser(id: string, payload: UpdateUserPayload): Promise<void> {
  const ref = doc(db, COLLECTION, id);
  // Firestore rejette les valeurs `undefined` : on les convertit en `null`.
  const sanitized: Record<string, unknown> = {};
  Object.entries(payload).forEach(([key, value]) => {
    sanitized[key] = value === undefined ? null : value;
  });
  await updateDoc(ref, {
    ...sanitized,
    updatedAt: new Date(),
  });
}

export async function createUser(row: ImportedUserRow): Promise<string> {
  const ref = await addDoc(collection(db, COLLECTION), {
    authUid: row.authUid ?? null,
    firstName: row.firstName,
    lastName: row.lastName,
    email: row.email ?? null,
    phone: row.phone ?? null,
    academy: row.academy ?? null,
    cap: row.cap ?? null,
    region: row.region ?? null,
    commune: row.commune ?? null,
    speciality: row.speciality ?? null,
    role: row.role,
    status: row.status,
    createdAt: serverTimestamp(),
    updatedAt: serverTimestamp(),
  });
  return ref.id;
}

export async function importUsers(rows: ImportedUserRow[]): Promise<string[]> {
  if (!rows.length) return [];

  const batch = writeBatch(db);
  const createdIds: string[] = [];

  rows.forEach((row) => {
    const ref = doc(collection(db, COLLECTION));
    createdIds.push(ref.id);
    batch.set(ref, {
      authUid: row.authUid ?? null,
      firstName: row.firstName,
      lastName: row.lastName,
      email: row.email ?? null,
      phone: row.phone ?? null,
      academy: row.academy ?? null,
      cap: row.cap ?? null,
      region: row.region ?? null,
      commune: row.commune ?? null,
      speciality: row.speciality ?? null,
      role: row.role,
      status: row.status,
      createdAt: serverTimestamp(),
      updatedAt: serverTimestamp(),
    });
  });

  await batch.commit();
  return createdIds;
}

function collapseUserStr(s: string): string {
  return s.trim().toLowerCase().replace(/\s+/g, ' ');
}

/** Clé stable pour dédoublonner un utilisateur (alignée sur les docs Firestore `users`). */
export function userImportDedupeKey(row: ImportedUserRow | Record<string, unknown>): string {
  const r = row as unknown as Record<string, unknown>;
  const email = r.email != null ? collapseUserStr(String(r.email)) : '';
  if (email) return `email::${email}`;
  const phoneRaw = r.phone != null ? String(r.phone).replace(/\s/g, '') : '';
  const phone = phoneRaw ? collapseUserStr(phoneRaw) : '';
  if (phone) return `phone::${phone}`;
  const authUid = r.authUid != null ? collapseUserStr(String(r.authUid)) : '';
  if (authUid) return `authuid::${authUid}`;
  const first = collapseUserStr(String(r.firstName ?? ''));
  const last = collapseUserStr(String(r.lastName ?? ''));
  return `name::${first}::${last}`;
}

export async function fetchExistingUserKeyToId(): Promise<Map<string, string>> {
  const map = new Map<string, string>();
  const snap = await getDocs(collection(db, COLLECTION));
  snap.docs.forEach((d) => {
    const data = d.data() as Record<string, unknown>;
    const key = userImportDedupeKey(data);
    if (!map.has(key)) {
      map.set(key, d.id);
    }
  });
  return map;
}

export interface ImportUsersRowsResult {
  idsByRow: string[];
  importedCount: number;
  skippedCount: number;
}

const USER_IMPORT_BATCH_MAX = 400;

export async function importUsersRowsUnique(
  rows: ImportedUserRow[]
): Promise<ImportUsersRowsResult> {
  if (!rows.length) {
    return { idsByRow: [], importedCount: 0, skippedCount: 0 };
  }

  const idsByRow = Array.from({ length: rows.length }, () => '');
  const existingKeyToId = await fetchExistingUserKeyToId();
  const pendingSets: Array<{ ref: ReturnType<typeof doc>; payload: Record<string, unknown> }> = [];
  let importedCount = 0;
  let skippedCount = 0;

  rows.forEach((row, index) => {
    const key = userImportDedupeKey(row);
    if (existingKeyToId.has(key)) {
      skippedCount += 1;
      return;
    }
    const ref = doc(collection(db, COLLECTION));
    idsByRow[index] = ref.id;
    existingKeyToId.set(key, ref.id);
    importedCount += 1;
    pendingSets.push({
      ref,
      payload: {
        authUid: row.authUid ?? null,
        firstName: row.firstName,
        lastName: row.lastName,
        email: row.email ?? null,
        phone: row.phone ?? null,
        academy: row.academy ?? null,
        cap: row.cap ?? null,
        region: row.region ?? null,
        commune: row.commune ?? null,
        speciality: row.speciality ?? null,
        role: row.role,
        status: row.status,
        createdAt: serverTimestamp(),
        updatedAt: serverTimestamp(),
      },
    });
  });

  for (let i = 0; i < pendingSets.length; i += USER_IMPORT_BATCH_MAX) {
    const slice = pendingSets.slice(i, i + USER_IMPORT_BATCH_MAX);
    const batch = writeBatch(db);
    slice.forEach(({ ref, payload }) => {
      batch.set(ref, payload);
    });
    await batch.commit();
  }

  return { idsByRow, importedCount, skippedCount };
}

export async function findUserByAuthUid(authUid: string): Promise<UserRow | null> {
  const snap = await getDocs(
    query(
      collection(db, COLLECTION),
      where('authUid', '==', authUid),
      limit(1)
    )
  );
  if (snap.empty) return null;
  const d = snap.docs[0];
  const data = d.data() as any;
  return {
    id: d.id,
    authUid: data.authUid,
    firstName: data.firstName,
    lastName: data.lastName,
    phone: data.phone,
    academy: data.academy,
    cap: data.cap,
    region: data.region,
    commune: data.commune,
    speciality: data.speciality,
    email: data.email,
    role: (data.role as UserRole | undefined) ?? 'UNKNOWN',
    status: data.status,
    createdAt: data.createdAt?.toDate ? data.createdAt.toDate() : undefined,
    updatedAt: data.updatedAt?.toDate ? data.updatedAt.toDate() : undefined,
  };
}

export async function findUserByEmail(email: string): Promise<UserRow | null> {
  const snap = await getDocs(
    query(
      collection(db, COLLECTION),
      where('email', '==', email),
      limit(1)
    )
  );
  if (snap.empty) return null;
  const d = snap.docs[0];
  const data = d.data() as any;
  return {
    id: d.id,
    authUid: data.authUid,
    firstName: data.firstName,
    lastName: data.lastName,
    phone: data.phone,
    academy: data.academy,
    cap: data.cap,
    region: data.region,
    commune: data.commune,
    speciality: data.speciality,
    email: data.email,
    role: (data.role as UserRole | undefined) ?? 'UNKNOWN',
    status: data.status,
    createdAt: data.createdAt?.toDate ? data.createdAt.toDate() : undefined,
    updatedAt: data.updatedAt?.toDate ? data.updatedAt.toDate() : undefined,
  };
}

export async function findUserByPhone(phone: string): Promise<UserRow | null> {
  const snap = await getDocs(
    query(
      collection(db, COLLECTION),
      where('phone', '==', phone),
      limit(1)
    )
  );
  if (snap.empty) return null;
  const d = snap.docs[0];
  const data = d.data() as any;
  return {
    id: d.id,
    authUid: data.authUid,
    firstName: data.firstName,
    lastName: data.lastName,
    phone: data.phone,
    academy: data.academy,
    cap: data.cap,
    region: data.region,
    commune: data.commune,
    speciality: data.speciality,
    email: data.email,
    role: (data.role as UserRole | undefined) ?? 'UNKNOWN',
    status: data.status,
    createdAt: data.createdAt?.toDate ? data.createdAt.toDate() : undefined,
    updatedAt: data.updatedAt?.toDate ? data.updatedAt.toDate() : undefined,
  };
}

